import {
  ExcalidrawElement,
  ExcalidrawImageElement,
  ExcalidrawTextElement,
  ExcalidrawLinearElement,
  ExcalidrawGenericElement,
  NonDeleted,
  TextAlign,
  GroupId,
  VerticalAlign,
  Arrowhead,
  ExcalidrawFreeDrawElement,
  FontFamilyValues,
  ExcalidrawTextContainer,
  ExcalidrawFrameElement,
  ExcalidrawEmbeddableElement,
  ExcalidrawMagicFrameElement,
  ExcalidrawIframeElement,
    ExcalidrawMindmapElement,
    ExcalidrawStickyNotesElement,
} from "./types";
import {
  arrayToMap,
  getFontString,
  getUpdatedTimestamp,
  isTestEnv,
} from "../utils";
import { randomInteger, randomId } from "../random";
import { bumpVersion, newElementWith } from "./mutateElement";
import { getNewGroupIdsForDuplication } from "../groups";
import { AppState } from "../types";
import { getElementAbsoluteCoords } from ".";
import { adjustXYWithRotation } from "../math";
import { getResizedElementAbsoluteCoords } from "./bounds";
import {
  getContainerElement,
  measureText,
  normalizeText,
  wrapText,
  getBoundTextMaxWidth,
  getDefaultLineHeight,
} from "./textElement";
import {
    DEFAULT_ELEMENT_PROPS,
    DEFAULT_FONT_FAMILY,
    DEFAULT_FONT_SIZE,
    DEFAULT_TEXT_ALIGN,
    DEFAULT_VERTICAL_ALIGN, MIME_TYPES,
    VERTICAL_ALIGN,
} from "../constants";
import { MarkOptional, Merge, Mutable } from "../utility-types";
import {
    createNoteElementByNote, extFromMineType,
    generateUUID2, isAudioAnnoElement,
    isDarkMode,
    isFileElement, isNoteElement, isPdfAnnoElement, isVideoAnnoElement,
    replaceOriginNoteDocHtml
} from "../../../../src/utils";
import {
    DEFAULT_TEXT_WIDTH,
    getFrameElementBackgroundColor,
    getShapeElementBackgroundColor,
    HAS_ENABLE_TIPTAP_TEXT, KEY_DEFAULT_TEXT_FONT_FAMILY, KEY_DEFAULT_TEXT_FONT_SIZE, KEY_FILE_DIR
} from "../../../../src/Const";
import {COLOR_PALETTE} from "../colors";
import {ipcInvoke} from "../../../../src/common/ipc";
import {isImageElement} from "./typeChecks";
import {getCardById, updateCard} from "../../../../model";

export type ElementConstructorOpts = MarkOptional<
  Omit<ExcalidrawGenericElement, "id" | "type" | "isDeleted" | "updated">,
  | "width"
  | "height"
  | "angle"
  | "groupIds"
  | "frameId"
  | "boundElements"
  | "seed"
  | "version"
  | "versionNonce"
  | "link"
  | "strokeStyle"
  | "fillStyle"
  | "strokeColor"
  | "backgroundColor"
  | "roughness"
  | "strokeWidth"
  | "roundness"
  | "locked"
  | "opacity"
>;

const _newElementBase = <T extends ExcalidrawElement>(
  type: T["type"],
  {
    x,
    y,
    strokeColor = DEFAULT_ELEMENT_PROPS.strokeColor,
    backgroundColor = DEFAULT_ELEMENT_PROPS.backgroundColor,
    fillStyle = DEFAULT_ELEMENT_PROPS.fillStyle,
    strokeWidth = DEFAULT_ELEMENT_PROPS.strokeWidth,
    strokeStyle = DEFAULT_ELEMENT_PROPS.strokeStyle,
    roughness = DEFAULT_ELEMENT_PROPS.roughness,
    opacity = DEFAULT_ELEMENT_PROPS.opacity,
    width = 0,
    height = 0,
    angle = 0,
    groupIds = [],
    frameId = null,
    roundness = null,
    boundElements = null,
    link = null,
    locked = DEFAULT_ELEMENT_PROPS.locked,
    ...rest
  }: ElementConstructorOpts & Omit<Partial<ExcalidrawGenericElement>, "type">,
) => {
    // console.log('newElement:'+type);
  // assign type to guard against excess properties
  const element: Merge<ExcalidrawGenericElement, { type: T["type"] }> = {
    id: rest.id || randomId(),
    type,
    x,
    y,
    width,
    height,
    angle,
    strokeColor,
    backgroundColor,
    fillStyle,
    strokeWidth,
    strokeStyle,
    roughness,
    opacity,
    groupIds,
    frameId,
    roundness,
    seed: rest.seed ?? randomInteger(),
    version: rest.version || 1,
    versionNonce: rest.versionNonce ?? 0,
    isDeleted: false as false,
    boundElements,
      created:getUpdatedTimestamp(),
    updated: getUpdatedTimestamp(),
    link,
    locked,
  };
  return element;
};

export const newElement = (
  opts: {
    type: ExcalidrawGenericElement["type"];
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawGenericElement> =>
  _newElementBase<ExcalidrawGenericElement>(opts.type, opts);

export const newEmbeddableElement = (
  opts: {
    type: "embeddable";
    validated: ExcalidrawEmbeddableElement["validated"];
      embedType: "text" | "card" | "page";
      frameId: string | null;
      name:any
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawEmbeddableElement> => {
  return {
    ..._newElementBase<ExcalidrawEmbeddableElement>("embeddable", opts),
    validated: opts.validated,
      videoNotes: [],
      embedType: opts.embedType || "page",
      frameId: opts.frameId || null,
      name:opts.name,
      customData:opts.customData
  };
};

export const newMindmapElement = (
    opts: {
        type: "mindmap";
    } & ElementConstructorOpts,
): NonDeleted<ExcalidrawMindmapElement> => {
    return {
        ..._newElementBase<ExcalidrawMindmapElement>("mindmap", opts),
    };
};

export const newStickyNotesElement = (
    opts: {
        type: "stickyNotes";
        fontSize?: number;
        fontFamily?: FontFamilyValues;
        containerId: string;
        text: string;
        textAlign: TextAlign;
    } & ElementConstructorOpts,
): NonDeleted<ExcalidrawStickyNotesElement> => {
    const fontFamily = opts.fontFamily || DEFAULT_FONT_FAMILY;
    const fontSize = opts.fontSize || DEFAULT_FONT_SIZE;
    const containerId = opts.containerId;
    const text = opts.text || "";
    return {
        ..._newElementBase<ExcalidrawStickyNotesElement>("stickyNotes", opts),
        fontSize,
        fontFamily,
        containerId,
        value: text,
        riched: true,
        textAlign: opts.textAlign || "left",
        customData:opts.customData
    };
};

export const newTextTiptapElement = (
    opts: {
        type: "text";
        fontSize?: number;
        fontFamily?: FontFamilyValues;
        containerId: string;
        text: string;
        textAlign: TextAlign;
    } & ElementConstructorOpts,
): NonDeleted<ExcalidrawStickyNotesElement> => {
    const fontFamily = opts.fontFamily || DEFAULT_FONT_FAMILY;
    const fontSize = opts.fontSize || DEFAULT_FONT_SIZE;
    const containerId = opts.containerId || "";
    const text = opts.text || "";
    return {
        ..._newElementBase<ExcalidrawStickyNotesElement>("text", opts),
        fontSize,
        fontFamily,
        containerId,
        value: text,
        riched: true,
        textAlign: opts.textAlign || "left",
        customData:opts.customData
    };
};

export const newIframeElement = (
  opts: {
    type: "iframe";
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawIframeElement> => {
  return {
    ..._newElementBase<ExcalidrawIframeElement>("iframe", opts),
  };
};

export const newFrameElement = (
  opts: {
    name?: string;
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawFrameElement> => {
  const frameElement = newElementWith(
    {
      ..._newElementBase<ExcalidrawFrameElement>("frame", opts),
      type: "frame",
        isLockRatio:false,
        sort:0,
        backgroundColor:getFrameElementBackgroundColor(),
      name: opts?.name || null,
    },
    {},
  );

  return frameElement;
};

export const newMagicFrameElement = (
  opts: {
    name?: string;
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawMagicFrameElement> => {
  const frameElement = newElementWith(
    {
      ..._newElementBase<ExcalidrawMagicFrameElement>("magicframe", opts),
      type: "magicframe",
      name: opts?.name || null,
    },
    {},
  );

  return frameElement;
};

/** computes element x/y offset based on textAlign/verticalAlign */
const getTextElementPositionOffsets = (
  opts: {
    textAlign: ExcalidrawTextElement["textAlign"];
    verticalAlign: ExcalidrawTextElement["verticalAlign"];
  },
  metrics: {
    width: number;
    height: number;
  },
) => {
  return {
    x:
      opts.textAlign === "center"
        ? metrics.width / 2
        : opts.textAlign === "right"
        ? metrics.width
        : 0,
    y: opts.verticalAlign === "middle" ? metrics.height / 2 : 0,
  };
};

export const newTextElement = (
  opts: {
    text: string;
    fontSize?: number;
    fontFamily?: FontFamilyValues;
    textAlign?: TextAlign;
    verticalAlign?: VerticalAlign;
    containerId?: ExcalidrawTextContainer["id"] | null;
    lineHeight?: ExcalidrawTextElement["lineHeight"];
    strokeWidth?: ExcalidrawTextElement["strokeWidth"];
      bold?: ExcalidrawTextElement["bold"];
      italic?: ExcalidrawTextElement["italic"];
      underline?: ExcalidrawTextElement["underline"];
      strikeout?: ExcalidrawTextElement["strikeout"];

      richText?: ExcalidrawTextElement["richText"];
      initialized?: ExcalidrawTextElement["initialized"];
      customData?:any
  } & ElementConstructorOpts & {
      creation?: string;
  },
): NonDeleted<ExcalidrawTextElement> => {
  const fontFamily = opts.fontFamily || DEFAULT_FONT_FAMILY;
  const fontSize = opts.fontSize || DEFAULT_FONT_SIZE;
  const lineHeight = opts.lineHeight || getDefaultLineHeight(fontFamily);
    let text = normalizeText(opts.text);
    let originalText = text;
    // 复制的文本元素，最大宽度700，转换成换行的文字
    if (opts.creation === "addTextFromPaste") {
        text = wrapText(text, getFontString({ fontFamily, fontSize }), 700);
    }
  const metrics = measureText(
    text,
    getFontString({ fontFamily, fontSize }),
    lineHeight,
  );
  const textAlign = opts.textAlign || DEFAULT_TEXT_ALIGN;
  const verticalAlign = opts.verticalAlign || DEFAULT_VERTICAL_ALIGN;
  const offsets = getTextElementPositionOffsets(
    { textAlign, verticalAlign },
    metrics,
  );

  const textElement = newElementWith(
    {
      ..._newElementBase<ExcalidrawTextElement>("text", opts),
      text:(!opts.containerId && HAS_ENABLE_TIPTAP_TEXT)?opts.text:text,
      fontSize,
      fontFamily,
      textAlign,
      verticalAlign,
      x: opts.x - offsets.x,
      y: opts.y - offsets.y,
      width: (!opts.containerId && HAS_ENABLE_TIPTAP_TEXT)?DEFAULT_TEXT_WIDTH:metrics.width,
      height: opts.height || metrics.height,
      baseline: metrics.baseline,
      containerId: opts.containerId || null,
        customData:opts.customData || null,
      originalText,
      lineHeight,
        strokeColor:isDarkMode()?COLOR_PALETTE.white:COLOR_PALETTE.black,
        richText: opts.richText,
        initialized: !!opts.initialized,
      styles:[]
    },
    {},
  );
  return textElement;
};

const getAdjustedDimensions = (
  element: ExcalidrawTextElement,
  nextText: string,
): {
  x: number;
  y: number;
  width: number;
  height: number;
  baseline: number;
} => {
  const {
    width: nextWidth,
    height: nextHeight,
    baseline: nextBaseline,
  } = measureText(nextText, getFontString(element), element.lineHeight);
  const { textAlign, verticalAlign } = element;
  let x: number;
  let y: number;
  if (
    textAlign === "center" &&
    verticalAlign === VERTICAL_ALIGN.MIDDLE &&
    !element.containerId
  ) {
    const prevMetrics = measureText(
      element.text,
      getFontString(element),
      element.lineHeight,
    );
    const offsets = getTextElementPositionOffsets(element, {
      width: nextWidth - prevMetrics.width,
      height: nextHeight - prevMetrics.height,
    });

    x = element.x - offsets.x;
    y = element.y - offsets.y;
  } else {
    const [x1, y1, x2, y2] = getElementAbsoluteCoords(element);

    const [nextX1, nextY1, nextX2, nextY2] = getResizedElementAbsoluteCoords(
      element,
      nextWidth,
      nextHeight,
      false,
    );
    const deltaX1 = (x1 - nextX1) / 2;
    const deltaY1 = (y1 - nextY1) / 2;
    const deltaX2 = (x2 - nextX2) / 2;
    const deltaY2 = (y2 - nextY2) / 2;

    [x, y] = adjustXYWithRotation(
      {
        s: true,
        e: textAlign === "center" || textAlign === "left",
        w: textAlign === "center" || textAlign === "right",
      },
      element.x,
      element.y,
      element.angle,
      deltaX1,
      deltaY1,
      deltaX2,
      deltaY2,
    );
  }

  return {
    width: nextWidth,
    height: nextHeight,
    baseline: nextBaseline,
    x: Number.isFinite(x) ? x : element.x,
    y: Number.isFinite(y) ? y : element.y,
  };
};

export const refreshTextDimensions = (
    textElement: ExcalidrawTextElement,
    text = textElement.text,
) => {
    if (textElement.isDeleted) {
        return;
    }
    const container = getContainerElement(textElement);
    // 在矩形等容器内容器中，需要根据容器的宽度进行换行
    if (container) {
        text = wrapText(
            text,
            getFontString(textElement),
            getBoundTextMaxWidth(container),
            textElement,
        );
    } else {
        // 获取容器的宽度
        let maxWidth = 700;

        // 如果存在初始化(文字元素创建了，并且有文本)，并且文字含有多行
        if (textElement.initialized && textElement.text.split("\n").length > 1) {
            maxWidth = textElement.width;
        }
        text = wrapText(text, getFontString(textElement), maxWidth);
    }

    const dimensions = getAdjustedDimensions(textElement, text);
    // 如果前后两次的文本一样，宽度就不更新了
    let width = dimensions.width;
    if (
        !container &&
        textElement.initialized &&
        textElement.text.split("\n").length > 1
    ) {
        width = textElement.width;
    }

    return { text, ...dimensions, width };
};

export const updateTextElement = (
  textElement: ExcalidrawTextElement,
  {
    text,
    isDeleted,
    originalText,
      type,
  }: {
    text: string;
    isDeleted?: boolean;
    originalText: string;
      type?: string;
  },
): ExcalidrawTextElement => {
  return newElementWith(textElement, {
    originalText,
    isDeleted: isDeleted ?? textElement.isDeleted,
    ...refreshTextDimensions(textElement, originalText),
  });
};

export const newFreeDrawElement = (
  opts: {
      type: "freedraw" | "highlighter";
    points?: ExcalidrawFreeDrawElement["points"];
    simulatePressure: boolean;
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawFreeDrawElement> => {
  return {
    ..._newElementBase<ExcalidrawFreeDrawElement>(opts.type, opts),
    points: opts.points || [],
    pressures: [],
    simulatePressure: opts.simulatePressure,
    lastCommittedPoint: null,
      isLockRatio:true,
  };
};

export const newLinearElement = (
  opts: {
    type: ExcalidrawLinearElement["type"];
    startArrowhead?: Arrowhead | null;
    endArrowhead?: Arrowhead | null;
    points?: ExcalidrawLinearElement["points"];
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawLinearElement> => {
  return {
    ..._newElementBase<ExcalidrawLinearElement>(opts.type, opts),
    points: opts.points || [],
    lastCommittedPoint: null,
    startBinding: null,
    endBinding: null,
    startArrowhead: opts.startArrowhead || null,
    endArrowhead: opts.endArrowhead || null,
  };
};

export const newImageElement = (
  opts: {
    type: ExcalidrawImageElement["type"];
    status?: ExcalidrawImageElement["status"];
    fileId?: ExcalidrawImageElement["fileId"];
    scale?: ExcalidrawImageElement["scale"];
      isFrameBackgroundImage?: ExcalidrawImageElement["isFrameBackgroundImage"];
  } & ElementConstructorOpts,
): NonDeleted<ExcalidrawImageElement> => {
  return {
    ..._newElementBase<ExcalidrawImageElement>("image", opts),
    // in the future we'll support changing stroke color for some SVG elements,
    // and `transparent` will likely mean "use original colors of the image"
    strokeColor: "#00000000",
      strokeStyle:'none',
    status: opts.status ?? "pending",
    fileId: opts.fileId ?? null,
    scale: opts.scale ?? [1, 1],
      isLockRatio:true,
      hasUpdate:true,
      isFrameBackgroundImage: opts.isFrameBackgroundImage,
      name:opts.name,
      customData:opts.customData
  };
};

// Simplified deep clone for the purpose of cloning ExcalidrawElement.
//
// Only clones plain objects and arrays. Doesn't clone Date, RegExp, Map, Set,
// Typed arrays and other non-null objects.
//
// Adapted from https://github.com/lukeed/klona
//
// The reason for `deepCopyElement()` wrapper is type safety (only allow
// passing ExcalidrawElement as the top-level argument).
const _deepCopyElement = (val: any, depth: number = 0) => {
  // only clone non-primitives
  if (val == null || typeof val !== "object") {
    return val;
  }

  const objectType = Object.prototype.toString.call(val);

  if (objectType === "[object Object]") {
    const tmp =
      typeof val.constructor === "function"
        ? Object.create(Object.getPrototypeOf(val))
        : {};
    for (const key in val) {
      if (val.hasOwnProperty(key)) {
        // don't copy non-serializable objects like these caches. They'll be
        // populated when the element is rendered.
        if (depth === 0 && (key === "shape" || key === "canvas")) {
          continue;
        }
        tmp[key] = _deepCopyElement(val[key], depth + 1);
      }
    }
    return tmp;
  }

  if (Array.isArray(val)) {
    let k = val.length;
    const arr = new Array(k);
    while (k--) {
      arr[k] = _deepCopyElement(val[k], depth + 1);
    }
    return arr;
  }

  // we're not cloning non-array & non-plain-object objects because we
  // don't support them on excalidraw elements yet. If we do, we need to make
  // sure we start cloning them, so let's warn about it.
  if (import.meta.env.DEV) {
    if (
      objectType !== "[object Object]" &&
      objectType !== "[object Array]" &&
      objectType.startsWith("[object ")
    ) {
      console.warn(
        `_deepCloneElement: unexpected object type ${objectType}. This value will not be cloned!`,
      );
    }
  }

  return val;
};

/**
 * Clones ExcalidrawElement data structure. Does not regenerate id, nonce, or
 * any value. The purpose is to to break object references for immutability
 * reasons, whenever we want to keep the original element, but ensure it's not
 * mutated.
 *
 * Only clones plain objects and arrays. Doesn't clone Date, RegExp, Map, Set,
 * Typed arrays and other non-null objects.
 */
export const deepCopyElement = <T extends ExcalidrawElement>(
  val: T,
): Mutable<T> => {
  return _deepCopyElement(val);
};

/**
 * utility wrapper to generate new id. In test env it reuses the old + postfix
 * for test assertions.
 */
export const regenerateId = (
  /** supply null if no previous id exists */
  previousId: string | null,
) => {
  if (isTestEnv() && previousId) {
    let nextId = `${previousId}_copy`;
    // `window.h` may not be defined in some unit tests
    if (
      window.h?.app
        ?.getSceneElementsIncludingDeleted()
        .find((el) => el.id === nextId)
    ) {
      nextId += "_copy";
    }
    return nextId;
  }
  return randomId();
};

/**
 * Duplicate an element, often used in the alt-drag operation.
 * NoteItem that this method has gotten a bit complicated since the
 * introduction of gruoping/ungrouping elements.
 * @param editingGroupId The current group being edited. The new
 *                       element will inherit this group and its
 *                       parents.
 * @param groupIdMapForOperation A Map that maps old group IDs to
 *                               duplicated ones. If you are duplicating
 *                               multiple elements at once, share this map
 *                               amongst all of them
 * @param element Element to duplicate
 * @param overrides Any element properties to override
 */
export const duplicateElement = <TElement extends ExcalidrawElement>(
  editingGroupId: AppState["editingGroupId"],
  groupIdMapForOperation: Map<GroupId, GroupId>,
  element: TElement,
  overrides?: Partial<TElement>,
) => {
  let copy = deepCopyElement(element);

  copy.id = regenerateId(copy.id);
  copy.boundElements = null;
  copy.updated = getUpdatedTimestamp();
  copy.seed = randomInteger();
  copy.groupIds = getNewGroupIdsForDuplication(
    copy.groupIds,
    editingGroupId,
    (groupId) => {
      if (!groupIdMapForOperation.has(groupId)) {
        groupIdMapForOperation.set(groupId, regenerateId(groupId));
      }
      return groupIdMapForOperation.get(groupId)!;
    },
  );
  if (copy.type == 'iframe' && !isFileElement(copy)){
      if (copy.customData?.generationData){
          if (isNoteElement(copy)){
              const newUUID = generateUUID2();
              //创建副本，
              getCardById(copy.customData.generationData.id).then(res=>{
                  if (res){
                      res.id = newUUID;
                      updateCard(res);
                      copy.customData.generationData.id = newUUID;
                  }
              })
          }else{
              copy.customData.generationData.id = copy.customData.generationData.id;//element.id;//保留原始的笔记ID...
          }

          // const szReplace = "<span style=\'font-size: 20px;font-weight: 600;margin-bottom: 8px\'>"+copy.customData.generationData.name+"</span>";
          // let szHtml = ''//copy.customData.generationData.html.replace(szReplace,"");
          // szHtml = replaceOriginNoteDocHtml(copy.customData.generationData.name,copy.customData.generationData.html);
          // copy.customData.generationData.name += '(Copy)';
          // copy.customData.generationData.html = createNoteElementByNote(copy.customData.generationData.name,szHtml);

          //todo 如何把这个变成同步的函数？？？？？？？？？？怎么搞？？？？？
          // const note = await window.methodGetNoteById(element.id);
          // if (note){
          //     copy.customData.generationData.name = note.name+'(Copy)';
          //     copy.customData.generationData.html = createNoteElementByNote(copy.customData.generationData.name,note.doc)
          // }
      }
  }
  //如果是图片复制一份.
    if (copy.type == 'image') {
        const arrayFiles = window.getAppFiles();
        const file = arrayFiles[copy.fileId];
        let szExt = '.'+extFromMineType(file.mimeType);
        let szUUID = generateUUID2();
        let szNewName = szUUID + szExt;
        let szDir = localStorage.getItem(KEY_FILE_DIR);
        ipcInvoke('copy-file', {forceRename: true, filePath: szDir+copy.fileId+szExt, newName:szNewName})?.then(res=>{
            //构建一个file加到集合...
            const newFile = {...file,id:szUUID};
            window.addImageFile(newFile);
        });
        copy.fileId = szUUID;


    }
  if (overrides) {
    copy = Object.assign(copy, overrides);
  }
  return copy;
};

/**
 * Clones elements, regenerating their ids (including bindings) and group ids.
 *
 * If bindings don't exist in the elements array, they are removed. Therefore,
 * it's advised to supply the whole elements array, or sets of elements that
 * are encapsulated (such as library items), if the purpose is to retain
 * bindings to the cloned elements intact.
 *
 * NOTE by default does not randomize or regenerate anything except the id.
 */
export const duplicateElements = (
  elements: readonly ExcalidrawElement[],
  opts?: {
    /** NOTE also updates version flags and `updated` */
    randomizeSeed: boolean,
  },
ignoreAnnoElement=false) => {
    const clonedElements: ExcalidrawElement[] = [];

    const origElementsMap = arrayToMap(elements);

    // used for for migrating old ids to new ids
    const elementNewIdsMap = new Map<
        /* orig */ ExcalidrawElement["id"],
        /* new */ ExcalidrawElement["id"]
    >();

    const maybeGetNewId = (id: ExcalidrawElement["id"]) => {
        // if we've already migrated the element id, return the new one directly
        if (elementNewIdsMap.has(id)) {
            return elementNewIdsMap.get(id)!;
        }
        // if we haven't migrated the element id, but an old element with the same
        // id exists, generate a new id for it and return it
        if (origElementsMap.has(id)) {
            const newId = regenerateId(id);
            elementNewIdsMap.set(id, newId);
            return newId;
        }
        // if old element doesn't exist, return null to mark it for removal
        return null;
    };

    const groupNewIdsMap = new Map</* orig */ GroupId, /* new */ GroupId>();

    for (const element of elements) {
        const clonedElement: Mutable<ExcalidrawElement> = _deepCopyElement(element);

        // if (ignoreAnnoElement){
        //     if (isPdfAnnoElement(element) || isVideoAnnoElement(element) || isAudioAnnoElement(element)){
        //         //这几种类型的不创建新的ID
        //         clonedElement.id = element.id;
        //     }else{
        //         clonedElement.id = maybeGetNewId(element.id)!;
        //     }
        // }else{
        //     clonedElement.id = maybeGetNewId(element.id)!;
        // }
        clonedElement.id = maybeGetNewId(element.id)!;

        if (opts?.randomizeSeed) {
            clonedElement.seed = randomInteger();
            bumpVersion(clonedElement);
        }

        if (clonedElement.groupIds) {
            clonedElement.groupIds = clonedElement.groupIds.map((groupId) => {
                if (!groupNewIdsMap.has(groupId)) {
                    groupNewIdsMap.set(groupId, regenerateId(groupId));
                }
                return groupNewIdsMap.get(groupId)!;
            });
        }

        if ("containerId" in clonedElement && clonedElement.containerId) {
            const newContainerId = maybeGetNewId(clonedElement.containerId);
            clonedElement.containerId = newContainerId;
        }

        if ("boundElements" in clonedElement && clonedElement.boundElements) {
            clonedElement.boundElements = clonedElement.boundElements.reduce(
                (
                    acc: Mutable<NonNullable<ExcalidrawElement["boundElements"]>>,
                    binding,
                ) => {
                    const newBindingId = maybeGetNewId(binding.id);
                    if (newBindingId) {
                        acc.push({...binding, id: newBindingId});
                    }
                    return acc;
                },
                [],
            );
        }

        if ("endBinding" in clonedElement && clonedElement.endBinding) {
            const newEndBindingId = maybeGetNewId(clonedElement.endBinding.elementId);
            clonedElement.endBinding = newEndBindingId
                ? {
                    ...clonedElement.endBinding,
                    elementId: newEndBindingId,
                }
                : null;
        }
        if ("startBinding" in clonedElement && clonedElement.startBinding) {
            const newEndBindingId = maybeGetNewId(
                clonedElement.startBinding.elementId,
            );
            clonedElement.startBinding = newEndBindingId
                ? {
                    ...clonedElement.startBinding,
                    elementId: newEndBindingId,
                }
                : null;
        }

        if (clonedElement.frameId) {
            clonedElement.frameId = maybeGetNewId(clonedElement.frameId);
        }

        //如果是图片，全部复制一份..
        if (isImageElement(clonedElement)){
            const arrayFiles = window.getAppFiles();
            const file = arrayFiles[clonedElement.fileId];
            let szExt = '.'+extFromMineType(file.mimeType);
            let szUUID = generateUUID2();
            let szNewName = szUUID + szExt;
            let szDir = localStorage.getItem(KEY_FILE_DIR);
            ipcInvoke('copy-file', {forceRename: true, filePath: szDir+clonedElement.fileId+szExt, newName:szNewName})?.then(res=>{
                //构建一个file加到集合...
                const newFile = {...file,id:szUUID};
                window.addImageFile(newFile);
            });
            clonedElement.fileId = szUUID;
        }

        clonedElements.push(clonedElement);
    }

    return clonedElements;
};
